import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { TreeService, TreeGroup } from '@schoolbelle/api/group-to-group';
import { LetterTargetService } from '@schoolbelle/api/letter-target';
import { Subscription, Subject, of, BehaviorSubject } from 'rxjs';
import { GroupMemberService, convertStudentParentlistToTableData } from '@schoolbelle/api/group-member';
import { Table } from '@schoolbelle/common/table';
import { get, maxBy } from 'lodash-es';
import { TableSearchService } from '../table-search/table-search.service';
import { GroupMember } from '@schoolbelle/api/group-member';
import { debounceTime, distinctUntilChanged, switchMap, take } from 'rxjs/operators';
import { VirtualScrollerComponent } from 'ngx-virtual-scroller';
import { GroupService } from '@schoolbelle/api/group';

@Component({
  selector: 'student-parent-tab',
  templateUrl: './student-parent-tab.component.html',
  styleUrls: ['./student-parent-tab.component.scss'],
  changeDetection:ChangeDetectionStrategy.OnPush,
})
export class SelectLetterTargetsStudentParentTabComponent implements OnInit {
  @ViewChild('scroll') scroll:VirtualScrollerComponent;
  table:Table = new Table();
  subscription = new Subscription();

  isCheckedAllChangeEvent = new BehaviorSubject<0 | 1 | 2>(0);
  get isCheckedAll() { return this.isCheckedAllChangeEvent.value; }
  set isCheckedAll(v) { this.isCheckedAllChangeEvent.next(v); }

  protected tableMap = new Map<number, Table>();
  protected currentTableChangeEvent = new Subject();
  currentGroup:TreeGroup;
  currentGroupId:number;
  constructor(
    public tree:TreeService,
    private member:GroupMemberService,
    private letterTarget:LetterTargetService,
    private tableSearch:TableSearchService,
    private cdRef:ChangeDetectorRef,
    public group:GroupService
  ) { 

  }

  ngOnInit() {
    this.subscription.add(
      this.tree.onOpen.subscribe((group_id)=>{
        this.updateTableContent(group_id);
        this.updateTableCheckedArea(group_id);
      })
    )  
    this.subscription.add(
      this.letterTarget.onChange
      .subscribe((e)=>{
        this.updateTableCheckedArea(this.tree.deepestOpenedGroupId);
      })
    )
    this.subscription.add(
      this.tableSearch.onSelect.subscribe(member=>{
        this.goToMember(member);
      })
    )


    const targetgroups = this.letterTarget.targets.map(target=>this.tree.root.find({group_id:target.group_id}));
    const firstDepth2target = targetgroups.find(group=>group && group.depth === 2);
    if (firstDepth2target) firstDepth2target.open();
    else this.tree.root.open();

    // track isCheckedAll
    this.subscription.add(
      this.letterTarget.onChange.pipe(
        debounceTime(100),
        // distinctUntilChanged(),
      ).subscribe(() => {
        const togglables = this.tree.root.subgroups.filter(subGroup => (subGroup.subgroups.length > 0));
        const checkedMap = togglables.reduce((acc, togglable) => {
          const checkedVal = this.letterTarget.isChecked(togglable.group_id, 'all') ||  this.letterTarget.isChecked(togglable.group_id, 'studentparent_all') || 0;
          acc[checkedVal] += 1;
          return acc;
        }, {
          0: 0,
          1: 0,
          2: 0,
        });

        if (checkedMap[2] === togglables.length) {
          this.isCheckedAll = 2;
        } else if (checkedMap[2] > 0 || checkedMap[1] > 0) {
          this.isCheckedAll = 1;
        } else {
          this.isCheckedAll = 0;
        }

        this.cdRef.detectChanges();
      })
    );
  }
  goToMember (member:GroupMember) {
    this.table.cells.filter(c=>c.matched).forEach(c=>c.matched = false);
    const group = this.tree.root.find(t=>t.group_id === member.group_id)
    if (!group) throw new Error('Group not found');
    of(null)
    .pipe(
      switchMap(()=>{
        if (group.group_id !== this.currentGroupId) {
          group.open();
          return this.tree.onOpen.pipe(take(1));
        }
        else {
          return of(null);
        }
      })
    )
    .subscribe(()=>{
      const cell = this.table.cells.find(c=>{
        const m = c.get('member');
        return m && m.member_id === member.member_id;
      });

      if (cell) {
        const i = cell.y;
        cell.matched = true;
        const {startIndex, endIndex} = this.scroll.viewPortInfo;
        const size = endIndex - startIndex;
        const alignToBeginning = this.table.y - size < i;
        this.scroll.scrollToIndex(i, alignToBeginning);
        this.cdRef.detectChanges();
      }        
    })
  }

  onSelectionChange ($event) {
    this.updateTargetScopes(this.tree.deepestOpenedGroupId);
  } 

  ngOnDestroy () {
    this.subscription.unsubscribe()
  }
  private updateTableContent (group_id):void {
    let studentlist = this.member.search({group_id:group_id, member_types:['student']});
    let parentlist = this.member.search({group_id:group_id, member_types:['parent']});
    const data = convertStudentParentlistToTableData(studentlist, parentlist);
    

    this.table.setX(0);
    this.table.setY(0);

    // table is built on removing all cells and recreating cells again for new data.
    this.table.setX(5);
    this.table.setY(get(maxBy(studentlist, 'member_num'), 'member_num'), {notifyResize:true});
    
    this.table.load(data);
  }
  private updateTableCheckedArea (group_id):void {
    const targetScopes:string = this.letterTarget.getScopes(group_id);
    const tableCheckedArea = this.convertLetterTargetScopesToTableArea('studentparent_', targetScopes, group_id);
    if (this.table.selectedArea !== tableCheckedArea) {
      this.table.selectedArea = tableCheckedArea;
      this.cdRef.detectChanges()
    }
  }
  private updateTargetScopes (group_id):void {
    const tableCheckedArea = this.table.selectedArea;
    const letterTargetScope = this.convertTableAreaToLetterTargetScopes('studentparent_', tableCheckedArea, group_id);
    this.letterTarget.setScopes(group_id, letterTargetScope, 'up');
  }
  private convertLetterTargetScopesToTableArea (prefix:'studentparent_'|'faculty_', scopes:string, group_id:number):string {
    return scopes.split(',').filter(s=>s)
    .map(scope => scope.replace(prefix, ''))
    .filter(scope=>!scope.match(/col_[^1-3]/))
    .map(scope=>{
      return scope.replace(/col_([\d]+)/, (matched, n)=>{
        switch(n) {
          case '1':
          return `col_5`;
          case '2':
          return `col_3`;
          case '3':
          return `col_4`;
          default :
          return matched;
        }
      })
    })
    .map(scope=>{
      // from : all, col_num, row_member_name, col_num_of_row_member_name
      // to   : all, col_num, row_num, col_num_of_row_num
      return scope.replace(/^(col_[\d]+)?(_of_)?row_(.+)$/, (matched, col_num, conj, URIComponentEncoded_member_name)=>{
        const m = this.member.search({group_id, member_types:['faculty', 'student'], member_name:decodeURIComponent(URIComponentEncoded_member_name)})[0];
        return m ? `${col_num || ''}${conj || ''}row_${m.member_num}` : '';
      })
    })
    .join(',')    
  }
  private convertTableAreaToLetterTargetScopes (tablename:'studentparent_'|'faculty_', areas:string, group_id:number):string {
    return areas.split(',').filter(a=>a)
    .filter(scope=>!scope.match(/col_[^3-5]/))
    .map(scope=>{
      return scope.replace(/col_([\d]+)/, (matched, n)=>{
        switch(n) {
          case '5':
          return `col_1`;
          case '3':
          return `col_2`;
          case '4':
          return `col_3`;
          default :
          return matched;
        }
      })
    })
    .map(scope=>{
      // from : all, col_num, row_num, col_num_of_row_num
      // to   : all, col_num, row_member_name, col_num_of_row_member_name
      return scope.replace(/^(col_[\d]+)?(_of_)?row_([\d]+)$/, (matched, col_num, conj, member_num)=>{
        const m = this.member.search({group_id, member_types:['student', 'faculty'], member_num:Number(member_num)})[0];
        return m ? `${col_num || ''}${conj || ''}row_${encodeURIComponent(m.member_name)}`:'';
      });
    })
    .map(area=>tablename + area)
    .join(',')    
  }  
  trackById (index, row) {
    return row.id;
  }
  toggleCol (x:number) {
    this.table.toggleAreaByXY(x, undefined);
    this.toggleNumNameCols();
  }
  toggleTable () {
    this.table.toggleAreaByXY(undefined, undefined);
    this.toggleNumNameCols();
  }
  isColumnSelected (i:number) {
    const col = this.table.getColumn(i);
    return col && col.selected && col.selected[0] === col;
  }  
  isTableSelected () {
    const table = this.table;
    return table && table.selected && table.selected[0] === table;
  }

  toggleAll() {
    const togglables = this.tree.root.subgroups.filter(subGroup => (subGroup.subgroups.length > 0));

    if (this.isCheckedAll) {
      togglables.forEach(togglable => {
        this.letterTarget.setScopes(togglable.group_id, '', 'both');
      })
    } else {
      togglables.forEach(togglable => {
        this.letterTarget.setScopes(togglable.group_id, 'studentparent_all', 'both');
      })
    }
  }

  toggleCell (x:number, y:number) {
    this.table.toggleAreaByXY(x, y);
    this.toggleNumNameCols();
    this.toggleNumNameCellsOfRow(y);
  }
  toggleRow (y:number) {
    this.table.toggleAreaByXY(undefined, y);
    this.toggleNumNameCellsOfRow(y);
  }
  private toggleNumNameCols () {
    const area = this.table.selectedArea.split(',');
    if (
      area.includes('col_3')
      && area.includes('col_4')
      && area.includes('col_5')
    ) {
      if (!area.includes('col_1')) this.table.toggleAreaByXY(0, undefined);
      if (!area.includes('col_2')) this.table.toggleAreaByXY(1, undefined);
    }
    else {
      if (area.includes('col_1')) this.table.toggleAreaByXY(0, undefined);
      if (area.includes('col_2')) this.table.toggleAreaByXY(1, undefined);

    }
  }
  private toggleNumNameCellsOfRow (y:number) {
    const area = this.table.selectedArea.split(',');
    if (
      (area.includes(`col_3`) || area.includes(`col_3_of_row_${y+1}`))
      && (area.includes(`col_4`) || area.includes(`col_4_of_row_${y+1}`))
      && (area.includes(`col_5`) || area.includes(`col_5_of_row_${y+1}`))
    ) {
      if (!area.includes(`col_1`) && !area.includes(`col_1_of_row_${y+1}`)) this.table.toggleAreaByXY(0, y);
      if (!area.includes(`col_2`) && !area.includes(`col_2_of_row_${y+1}`)) this.table.toggleAreaByXY(1, y);
    } 
    else {
      if (area.includes(`col_1`) || area.includes(`col_1_of_row_${y+1}`)) this.table.toggleAreaByXY(0, y);
      if (area.includes(`col_2`) || area.includes(`col_2_of_row_${y+1}`)) this.table.toggleAreaByXY(1, y);
    }

  }

}
